import type { ContractListItem, ContractState, Lookups, Me } from '@ft/shared';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useNewShortcut } from '../components/CommandPalette';
import { ContractForm } from '../components/ContractForm';
import { SavedViews } from '../components/SavedViews';
import { Cust, Loading, useLoad, useToast } from '../components/ui';
import { useI18n, type T } from '../i18n';
import { canEdit as canEditArea, getAreaName } from '../lib/area';
import { dfmt, eur, pct } from '../lib/format';
import { useRememberedFilter } from '../lib/remember';

const TONE: Record<ContractState, string> = { noBudget: 'neu', ok: 'pos', nearlyUsed: 'crit', exceeded: 'neg' };

export function ContractStatePill({ c, t }: { c: Pick<ContractListItem, 'state' | 'endsInDays'>; t: T }) {
  return (
    <>
      <span className={`pill ${TONE[c.state]}`}>{t(`ct.state.${c.state}`)}</span>
      {c.endsInDays !== null && <span className={`pill ${c.endsInDays < 0 ? 'neu' : c.endsInDays <= 30 ? 'crit' : 'info'}`} style={{ marginLeft: 4 }}>{c.endsInDays < 0 ? t('ct.expired') : t('ct.endsIn', { n: c.endsInDays })}</span>}
    </>
  );
}

/** Bestellt im Verhältnis zum Budget; Strich = 100 %. */
export function BudgetBar({ ordered, budget }: { ordered: number; budget: number | null }) {
  if (!budget) return <span className="muted">–</span>;
  const u = ordered / budget;
  const col = u > 1 ? 'var(--neg)' : u >= 0.9 ? 'var(--crit)' : 'var(--accent)';
  return (
    <div className="use">
      <div className="meter"><i style={{ width: `${Math.min(100, u * 100)}%`, background: col }} /></div>
      <span className="num">{pct(u)}</span>
    </div>
  );
}

/** Hauptverträge (Rahmenverträge): Budget, Projekte, was durch Bestellungen schon verbraucht ist, Ablage. */
export function ContractsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  useRememberedFilter('contracts');
  const { data, error } = useLoad<ContractListItem[]>('/api/contracts');
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const [creating, setCreating] = useState(false);
  const [showFree, setShowFree] = useState(false);
  const [toast] = useToast();
  const canEdit = canEditArea(me);
  useNewShortcut(useCallback(() => setCreating(true), []), canEdit);

  const f = { c: sp.get('kunde') ?? '', s: sp.get('status') ?? '', q: sp.get('q') ?? '', a: sp.get('bereich') ?? '' };
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const customers = [...new Set((data ?? []).map((c) => c.customer.name))].sort();
  const areas = [...new Set((data ?? []).flatMap((c) => c.areas.map((a) => a.area.name)))].sort();

  const list = useMemo(() => (data ?? []).filter((c) =>
    (!f.c || c.customer.name === f.c) &&
    (!f.a || c.areas.some((a) => a.area.name === f.a)) &&
    (!f.s || (f.s === 'notFiled' ? !c.filed : f.s === 'ending' ? c.endsInDays !== null && c.endsInDays >= 0 : c.state === f.s)) &&
    (!f.q || [c.name, c.contractNumber, c.customer.name, ...c.projects.map((p) => p.project.name)].join(' ').toLowerCase().includes(f.q.toLowerCase()))), [data, f.c, f.s, f.q, f.a]);
  const sum = (k: 'budgetCents' | 'orderedCents' | 'billedCents' | 'restCents') => list.reduce((s, c) => s + (c[k] ?? 0), 0);

  // Projekte ohne Hauptvertrag (Hinweis unten)
  const inContract = new Set((data ?? []).flatMap((c) => c.projects.map((p) => p.project.id)));
  const free = (lookups?.projects ?? []).filter((p) => !inContract.has(p.id) && (!f.c || lookups?.customers.find((x) => x.id === p.customerId)?.name === f.c));

  return (
    <section className="page">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('ct.title')}</h1></div>
        <span className="spacer" />
        <SavedViews />
        {canEdit && <button className="btn primary" onClick={() => setCreating(true)} disabled={!lookups}>{t('ct.new')}</button>}
      </div>
      <p className="muted lead">{t('ct.lead')}</p>

      <div className="fbar">
        <div className="fld"><label htmlFor="ct-q">{t('common.search')}</label><input id="ct-q" type="search" value={f.q} onChange={(e) => set('q', e.target.value)} placeholder={t('ct.searchPh')} /></div>
        <div className="fld"><label htmlFor="ct-c">{t('common.customer')}</label><select id="ct-c" value={f.c} onChange={(e) => set('kunde', e.target.value)}><option value="">{t('common.all')}</option>{customers.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="fld"><label htmlFor="ct-s">{t('common.status')}</label>
          <select id="ct-s" value={f.s} onChange={(e) => set('status', e.target.value)}>
            <option value="">{t('common.all')}</option>
            {(['ok', 'nearlyUsed', 'exceeded', 'noBudget'] as const).map((s) => <option key={s} value={s}>{t(`ct.state.${s}`)}</option>)}
            <option value="ending">{t('ct.filterEnding')}</option>
            <option value="notFiled">{t('ct.filterNotFiled')}</option>
          </select>
        </div>
        {areas.length > 1 && <div className="fld"><label htmlFor="ct-a">{t('ct.col.area')}</label><select id="ct-a" value={f.a} onChange={(e) => set('bereich', e.target.value)}><option value="">{t('common.all')}</option>{areas.map((a) => <option key={a}>{a}</option>)}</select></div>}
        {(f.c || f.s || f.q || f.a) && <button className="btn ghost" onClick={() => setSp(new URLSearchParams(), { replace: true })}>{t('common.reset')}</button>}
      </div>

      {!data ? <Loading error={error} /> : (
        <div className="box">
          <div className="tablewrap">
            {!list.length ? <div className="empty">{data.length ? t('ct.emptyFilter') : t('ct.empty')}</div> : (
              <table>
                <thead><tr>
                  <th>{t('ct.col.contract')}</th><th>{t('common.customer')}</th><th>{t('ct.col.areas')}</th><th>{t('ct.col.projects')}</th><th>{t('ct.col.term')}</th>
                  <th className="r">{t('ct.col.budget')}</th><th className="r">{t('ct.col.ordered')}</th><th className="r">{t('ct.col.rest')}</th>
                  <th style={{ minWidth: 130 }}>{t('ct.col.usage')}</th><th>{t('common.status')}</th><th>{t('ct.col.filed')}</th>
                </tr></thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id} className="click" tabIndex={0} onClick={() => nav(`/vertraege/${c.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/vertraege/${c.id}`); }}>
                      <td><b style={{ fontWeight: 600 }}>{c.name}</b>{c.contractNumber && <div className="muted small mono">{c.contractNumber}</div>}</td>
                      <td><Cust name={c.customer.name} /></td>
                      <td className="small">{c.areas.map((a) => a.area.name).join(', ') || (c.externalCents ? '' : <span className="muted">–</span>)}{c.externalCents > 0 && <div className="muted">{t('ct.extMore')}</div>}</td>
                      <td className="small">{c.projects.length ? c.projects.map((p) => p.project.name).join(', ') : <span className="muted">{t('ct.noProjects')}</span>}</td>
                      <td className="num small nowrap">{c.validFrom || c.validTo ? `${dfmt(c.validFrom)} – ${dfmt(c.validTo)}` : '–'}</td>
                      <td className="r num">{c.budgetCents !== null ? eur(c.budgetCents) : '–'}</td>
                      <td className="r num">{eur(c.orderedCents)}</td>
                      <td className="r num" style={c.restCents !== null && c.restCents < 0 ? { color: 'var(--neg)' } : undefined}>{c.restCents !== null ? eur(c.restCents) : '–'}</td>
                      <td><BudgetBar ordered={c.orderedCents} budget={c.budgetCents} /></td>
                      <td className="nowrap"><ContractStatePill c={c} t={t} /></td>
                      <td className="small">{c.filed ? <span className="pill pos">{t('ct.filed')}</span> : <span className="pill crit" title={[!c.sharepointUrl && t('ct.missingLink'), !c.hasPdf && t('ct.missingPdf')].filter(Boolean).join(', ')}>{t('ct.notFiled')}</span>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td colSpan={5}>{t('common.sum')}</td>
                  <td className="r num">{eur(sum('budgetCents'))}</td><td className="r num">{eur(sum('orderedCents'))}</td><td className="r num">{eur(sum('restCents'))}</td><td colSpan={3} />
                </tr></tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {lookups && data && free.length > 0 && (
        <div className="box">
          <div className="panel-h">
            <h2>{t('ct.freeTitle', { n: free.length })}</h2><span className="hint">{t('ct.freeHint')}</span><span className="spacer" />
            <button className="btn ghost" aria-expanded={showFree} onClick={() => setShowFree((v) => !v)}>{showFree ? t('sync.hide') : t('ct.freeShow')}</button>
          </div>
          {showFree && (
            <ul className="chips" style={{ padding: '0 16px 14px', margin: 0, listStyle: 'none' }}>
              {free.map((p) => (
                <li key={p.id}><Link className="chip" style={{ textDecoration: 'none' }} to={`/bestellungen?projekt=${encodeURIComponent(p.name)}`}>{lookups.customers.find((c) => c.id === p.customerId)?.name} · {p.name}</Link></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creating && lookups && <ContractForm contract={null} lookups={lookups} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); nav(`/vertraege/${id}`); }} />}
      {toast}
    </section>
  );
}
